import moment from "moment";
import momentTz from "moment-timezone";

import {
  DayOfWeek,
  FormatDaysOfWeekArgs,
  WeekdayAbbreviation,
  WeekdayLetter,
} from "@campgladiator/cg-common.types.types";
import { NOW_IN_MILLI_SECONDS } from "@campgladiator/cg-common.utility.times";

export function getAbbreviatedWeekdayFromLetter(
  dayLetter: WeekdayLetter
): WeekdayAbbreviation {
  switch (dayLetter) {
    case "M":
      return "Mon";

    case "T":
      return "Tue";

    case "W":
      return "Wed";

    case "Th":
      return "Thu";

    case "F":
      return "Fri";

    case "S":
      return "Sat";

    case "Su":
      return "Sun";
  }
}

export const createDateWithTimeZone = (
  timeZone: string,
  date: string,
  time: string
) => {
  const dateTime = `${date} ${time}`;
  return momentTz.tz(dateTime, timeZone).toISOString();
};

export const militaryTimeToMeridianTime = (time: string) =>
  moment(time, "HH:mm:ss").format("h:mm A");

export const formatMeridianWithLocalTZ = (date: string) =>
  moment.utc(date).local().format("hh:mm A");

export const ISO8601ToLocaleDateString = (date: Date) =>
  date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

export const currentLocalDateInISO8601 = new Date().toISOString().split("T")[0];

export const twentyFourHourTimeStringToDate = (time: string): Date => {
  const date = new Date();
  const [hours, minutes] = time.split(":");
  date.setHours(parseInt(hours));
  date.setMinutes(parseInt(minutes));
  return date;
};

export const formatDateString = (fullDate: string): string => {
  const newDate = new Date(fullDate).toLocaleDateString("en-US");
  if (newDate === "Invalid Date") return "";
  return newDate;
};

export const formatTime = (time: string): string => {
  return twentyFourHourTimeStringToDate(time).toLocaleTimeString([], {
    hour: "numeric",
    minute: "numeric",
  });
};

export const getDayOfWeekName = (day: number): string | undefined => {
  const weekday = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return weekday[day];
};

const daysMap = (abbreviation: boolean) => ({
  MONDAY: abbreviation ? "M" : "Mon",
  TUESDAY: abbreviation ? "T" : "Tue",
  WEDNESDAY: abbreviation ? "W" : "Wed",
  THURSDAY: abbreviation ? "Th" : "Thu",
  FRIDAY: abbreviation ? "F" : "Fri",
  SATURDAY: abbreviation ? "S" : "Sat",
  SUNDAY: abbreviation ? "Su" : "Sun",
});

export const getFormattedWeekDays = ({
  daysOfTheWeek,
  abbreviation = false,
}: FormatDaysOfWeekArgs): string => {
  const days = daysMap(abbreviation);

  const daysToUppercase = daysOfTheWeek.toUpperCase();
  const daysAsArray = daysToUppercase.split(",") as DayOfWeek[];
  const dayAbbreviationsAsString = daysAsArray
    .map((day) => days[day])
    .join(", ");

  return dayAbbreviationsAsString;
};

export const convert24to12TimeWithoutSeconds = (time: any) => {
  time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time,
  ];

  if (time.length > 1) {
    time = time.slice(1);
    time[5] = +time[0] < 12 ? " AM" : " PM";
    time[0] = +time[0] % 12 || 12;
  }

  return time.join("").replace(":00", "");
};

/**
 * @param date example input: '2022-12-05'
 * @returns '12/05'
 */
export const getFormattedMonthDay = (date: string) => {
  const timeArray = date.split("-");
  return `${timeArray[1]}/${timeArray[2]}`;
};

export const getFormattedDay = (date: string) => {
  let day = date.split("-")[2];

  if (day.charAt(0) === "0") day = day.charAt(1);

  switch (day) {
    case "1":
      return `${day}st`;

    case "2":
      return `${day}nd`;

    case "3":
      return `${day}rd`;

    case "21":
      return `${day}st`;

    case "22":
      return `${day}nd`;

    case "23":
      return `${day}rd`;

    case "31":
      return `${day}st`;

    default:
      return `${day}th`;
  }
};

export const calculateFutureSession = (startDate: string | null) => {
  if (startDate) {
    const startDateInMilliseconds = new Date(startDate).getTime();
    if (NOW_IN_MILLI_SECONDS < startDateInMilliseconds) {
      return startDate;
    }
  }

  return null;
};

export const formatAbbreviatedMonth = (startDate: string) => {
  const dates = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  if (startDate) {
    const dateNumber = parseInt(startDate.split("-")[1]) - 1;
    return dates[dateNumber];
  }

  return null;
};

/**
 *
 * @param startDate 2012-09-10T05:00:00Z
 * @returns Mon, Sep. 10, 2012
 */
export const convertToWeekMonthDayYearFormat = (startDate: string) => {
  const date = new Date(startDate);

  let formattedDate = `${getDayOfWeekName(date.getDay())?.substring(0, 3)}, `;
  formattedDate += `${formatAbbreviatedMonth(startDate)}. `;
  formattedDate += `${date.getDate()}, `;
  formattedDate += `${date.getFullYear()}`;
  return formattedDate;
};

/**
 * Converts a given date string to a week month day format.
 * @param startDate - The date string to be converted.
 * @param substring - Optional parameter to specify the length of the day of week name to be returned.
 * @returns The formatted date string in the format "DAYOFWEEK MONTH/DAY".
 */
export const convertToWeekMonthDayFormat = (
  startDate: string,
  shortDayOfWeek?: boolean
) => {
  const date = moment.utc(startDate);

  let dayOfWeekName = date.format("dddd");
  if (shortDayOfWeek) {
    dayOfWeekName = dayOfWeekName?.substring(0, 3);
  }
  let formattedDate = `${dayOfWeekName} `;
  formattedDate += date.format("MM/DD");
  return formattedDate.toUpperCase();
};
/**
 *
 * @param startDate 2012-09-10T05:00:00Z
 * @returns Sep. 10
 */
export const convertToMonthDayFormat = (startDate: string) => {
  const date = new Date(startDate);
  return `${formatAbbreviatedMonth(startDate)}. ${date.getDate()}`;
};

/**
 *
 * @param startDate '2012-09-10'
 * @param addDays 30
 * @returns Oct. 10
 */
export const addDaysToStartDateReturnMonthDayFormat = (
  startDate: string,
  addDays?: number
) => {
  const date = new Date(startDate);

  if (addDays) date.setDate(date.getDate() + addDays);

  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();

  return month + ". " + day;
};

/**
 *
 * @param startDate '2012-09-10'
 * @param addDays 30
 * @returns 10/10/2012
 */
export const addDaysToStartDateReturnMMDDYYYY = (
  startDate: string,
  addDays?: number
) => {
  const date = new Date(startDate);

  if (addDays) date.setDate(date.getDate() + addDays);

  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

/**
 *
 * @param startDate 2012-09-10
 * @returns Oct. 10
 */
export const addAMonthAndConvertToMonthDayFormat = (startDate: string) => {
  const inputDate = new Date(startDate);
  const nextMonth = new Date(inputDate.getFullYear(), inputDate.getMonth() + 1);
  const nextMonthLastDay = new Date(
    inputDate.getFullYear(),
    inputDate.getMonth() + 2,
    0
  ).getDate();
  const inputDayOfMonth = inputDate.getDate();
  const formattedDayOfMonth =
    inputDayOfMonth > nextMonthLastDay ? nextMonthLastDay : inputDayOfMonth;
  const formattedNextMonthDate = convertToMonthDayFormat(
    `${nextMonth.getFullYear()}-${
      nextMonth.getMonth() + 1
    }-${formattedDayOfMonth.toString().padStart(2, "0")}`
  );
  return formattedNextMonthDate;
};

/**
 *
 * @returns '2023-03-27'
 */
export const getActualDate = () => new Date().toISOString().slice(0, 10);

/**
 * Format XX/YYYY in order to use in input field as a mask
 * @param input string
 * @returns formated string 'MM/YYYY'
 */
export const formatMonthAndYear = (input: string) => {
  const digitsOnly = input.replace(/\D/g, "");
  if (digitsOnly.length === 0) return undefined;
  const month = digitsOnly.substring(0, 2);
  const year = digitsOnly.substring(2, 4);
  if (month && !year) {
    return month + "/";
  }
  if (month && year) {
    return month + "/" + year;
  }
  return input;
};

/**
 *
 * @param input needs to be in the following formart: 'MM/YYYY'
 * @returns Object with month and year separated
 */
export const getMonthAndYearFromFormat = (input: string) => {
  const inputSplit = input?.split("/");
  const inputMonth = inputSplit ? inputSplit[0] : undefined;
  const inputYear = inputSplit ? `20${inputSplit[1]}` : undefined;
  return { month: inputMonth, year: inputYear };
};

export const addDaysToISOstringDate = (date: string, days: number) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

export const subtractDaysToISOstringDate = (date: string, days: number) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - days);
  return newDate;
};

export const subtractXDaysFromISOstringDate = (
  date: string,
  noOfDays: number
) => {
  const newDate = new Date(date);
  const formattedDate = new Date(
    new Date().setDate(newDate.getDate() - noOfDays)
  );
  return formattedDate.toISOString();
};

export const getCurrentDateWithLastTime = () => {
  const today = moment.utc().local();
  today.set({
    hour: 23,
    minute: 59,
  });
  return today.toISOString();
};

export const DAYS_OF_WEEK = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

export const getFormattedDateWithOrdinalSuffix = (dateString: string) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dateParts = dateString.split("-");
  const year = dateParts[0];
  const monthIndex = parseInt(dateParts[1]) - 1;
  const day = parseInt(dateParts[2]);

  let daySuffix;
  if (day === 1 || day === 21 || day === 31) {
    daySuffix = "st";
  } else if (day === 2 || day === 22) {
    daySuffix = "nd";
  } else if (day === 3 || day === 23) {
    daySuffix = "rd";
  } else {
    daySuffix = "th";
  }

  const formattedDate = `${months[monthIndex]} ${day}${daySuffix}.`;

  return formattedDate;
};

export const getTotalDaysInMonth = (month: number) => {
  const nextMonth = (month + 1) % 12;
  const firstDayOfNextMonth = new Date(new Date().getFullYear(), nextMonth, 1);
  const lastDayOfCurrentMonth = new Date(firstDayOfNextMonth.getTime() - 1);
  const totalDays = lastDayOfCurrentMonth.getDate();
  return totalDays;
};

export const isDatesSameOrAfter = (firstDate: string, secondDate: string) => {
  const firstMomentDate = moment(firstDate);
  const secondMomentDate = moment(secondDate);
  return secondMomentDate.isSameOrAfter(firstMomentDate);
};

export const getDayFromMinutes = (minutes: number) =>
  Math.ceil(Math.floor(minutes / 1440));

export const getMinutesSinceDate = (dateString: string) => {
  const currentDate = moment().utc().local();
  const startDate = moment(dateString).utc().local();
  const minutesSinceDate = currentDate.diff(startDate, "minutes");
  return minutesSinceDate;
};

export const getFormattedDateRange = (date1: string, date2: string) => {
  const startDate = moment(date1).utc().local();
  const endDate = moment(date2).utc().local();
  const formattedStartDate = startDate.format("MMM Do");
  const formattedEndDate = endDate.format("MMM Do");
  return `${formattedStartDate} to ${formattedEndDate}`;
};
